import React, { useState, useRef } from 'react';
import { useIsFocused } from '@react-navigation/native';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  Alert,
  Switch,
  Modal,
  I18nManager,
  Platform,
  Dimensions,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import logger from '../utils/logger';
import { deviceApi } from '../services/api';
import { siteApi, zoneApi } from '../services/api';
import { alarmRulesApi } from '../services/alarmRulesApi';
import { DeviceType, getDeviceTypeLabel, getDeviceTypeIcon } from '../constants/deviceTypes';
import MQTTCredentialsModal from '../components/MQTTCredentialsModal';
import { canManageDevices } from '../utils/permissions';

const { width } = Dimensions.get('window');

interface Step {
  id: number;
  title: string;
  titleAr: string;
  icon: string;
  completed: boolean;
}

export default function AddDeviceWizardScreen({ navigation }: any) {
  const { t, i18n } = useTranslation();
  const { theme } = useTheme();
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const isSavingRef = useRef(false);

  // P0: focus guard + throttle — prevent dual-Modal crash on iOS 17 (RNSScreen.mm:1968)
  const isFocused = useIsFocused();
  const isPresentingRef = useRef(false);

  const closeAllPickers = () => {
    setShowDeviceTypePicker(false);
    setShowSitePicker(false);
    setShowZonePicker(false);
  };

  const openCredentials = () => {
    if (!isFocused || isPresentingRef.current) return;
    isPresentingRef.current = true;
    closeAllPickers();
    setShowCredentialsModal(true);
    setTimeout(() => {
      isPresentingRef.current = false;
    }, 800);
  };

  const openPicker = (setter: React.Dispatch<React.SetStateAction<boolean>>) => {
    if (!isFocused || isPresentingRef.current) return;
    isPresentingRef.current = true;
    setShowCredentialsModal(false);
    closeAllPickers();
    setter(true);
    setTimeout(() => {
      isPresentingRef.current = false;
    }, 600);
  };

  const isArabic = i18n.language === 'ar';
  const isRTL = I18nManager.isRTL || isArabic;

  // Step 1: Basic Info
  const [name, setName] = useState('');
  const [deviceType, setDeviceType] = useState<DeviceType>(DeviceType.MULTI_SENSOR);
  const [hasBattery, setHasBattery] = useState(false);
  const [showDeviceTypePicker, setShowDeviceTypePicker] = useState(false);

  // Step 2: Location
  const [sites, setSites] = useState<any[]>([]);
  const [zones, setZones] = useState<any[]>([]);
  const [selectedSiteId, setSelectedSiteId] = useState<string | null>(null);
  const [selectedZoneId, setSelectedZoneId] = useState<string | null>(null);
  const [showSitePicker, setShowSitePicker] = useState(false);
  const [showZonePicker, setShowZonePicker] = useState(false);
  const [loadingSites, setLoadingSites] = useState(false);
  const [loadingZones, setLoadingZones] = useState(false);

  // Step 3: Tank Config (if applicable)
  const [tankCapacityLiters, setTankCapacityLiters] = useState('');
  const [tankHeightCm, setTankHeightCm] = useState('');
  const [tankDiameterCm, setTankDiameterCm] = useState('');
  const [tankAlertsEnabled, setTankAlertsEnabled] = useState(false);
  const [tankAlertLowCm, setTankAlertLowCm] = useState('');
  const [tankAlertHighCm, setTankAlertHighCm] = useState('');

  // Step 4: Credentials
  const [createdDevice, setCreatedDevice] = useState<any>(null);
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);

  const isTankDevice =
    deviceType === DeviceType.LEVEL ||
    deviceType === DeviceType.WATER ||
    deviceType === DeviceType.DIESEL;

  const steps: Step[] = [
    {
      id: 1,
      title: 'Basic Info',
      titleAr: 'المعلومات الأساسية',
      icon: 'information-circle',
      completed: currentStep > 1,
    },
    { id: 2, title: 'Location', titleAr: 'الموقع', icon: 'location', completed: currentStep > 2 },
    {
      id: 3,
      title: isTankDevice ? 'Tank Setup' : 'Configuration',
      titleAr: isTankDevice ? 'إعداد الخزان' : 'الإعداد',
      icon: isTankDevice ? 'water' : 'settings',
      completed: currentStep > 3,
    },
    {
      id: 4,
      title: 'Credentials',
      titleAr: 'بيانات الاتصال',
      icon: 'key',
      completed: currentStep > 4,
    },
  ];

  // Permission check — redirect if user cannot manage devices
  React.useEffect(() => {
    if (!canManageDevices(user?.roles || [])) {
      Alert.alert(
        isArabic ? 'غير مصرح' : 'Unauthorized',
        isArabic ? 'ليس لديك صلاحية لإضافة أجهزة' : 'You do not have permission to add devices',
        [{ text: isArabic ? 'رجوع' : 'Go Back', onPress: () => navigation.goBack() }],
      );
    }
  }, [user]);

  // Load sites on mount
  React.useEffect(() => {
    loadSites();
  }, []);

  // Load zones when site selected
  React.useEffect(() => {
    if (selectedSiteId) {
      loadZones(selectedSiteId);
    }
  }, [selectedSiteId]);

  const loadSites = async () => {
    try {
      setLoadingSites(true);
      if (!user?.orgId) return;
      const data = await siteApi.getSites(user.orgId);
      setSites(data);
    } catch (error) {
      logger.error('Failed to load sites:', error);
      Alert.alert(
        isArabic ? 'خطأ' : 'Error',
        isArabic ? 'فشل تحميل المواقع' : 'Failed to load sites',
      );
    } finally {
      setLoadingSites(false);
    }
  };

  const loadZones = async (siteId: string) => {
    try {
      setLoadingZones(true);
      if (!user?.orgId) return;
      const data = await zoneApi.getZones(user.orgId, siteId);
      setZones(data);
      // Auto-select first zone if only one
      if (data.length === 1) {
        setSelectedZoneId(data[0].id);
      }
    } catch (error) {
      logger.error('Failed to load zones:', error);
      Alert.alert(
        isArabic ? 'خطأ' : 'Error',
        isArabic ? 'فشل تحميل المناطق' : 'Failed to load zones',
      );
    } finally {
      setLoadingZones(false);
    }
  };

  const validateStep = (step: number): boolean => {
    switch (step) {
      case 1:
        if (!name.trim()) {
          Alert.alert(
            isArabic ? 'خطأ' : 'Error',
            isArabic ? 'الرجاء إدخال اسم الجهاز' : 'Please enter device name',
          );
          return false;
        }
        return true;

      case 2:
        if (!selectedSiteId) {
          Alert.alert(
            isArabic ? 'خطأ' : 'Error',
            isArabic ? 'الرجاء اختيار الموقع' : 'Please select a site',
          );
          return false;
        }
        if (!selectedZoneId) {
          Alert.alert(
            isArabic ? 'خطأ' : 'Error',
            isArabic ? 'الرجاء اختيار المنطقة' : 'Please select a zone',
          );
          return false;
        }
        return true;

      case 3:
        // Tank validation if applicable
        if (isTankDevice) {
          if (!tankCapacityLiters.trim() || !tankHeightCm.trim()) {
            Alert.alert(
              isArabic ? 'تحذير' : 'Warning',
              isArabic
                ? 'يُنصح بإدخال سعة الخزان والارتفاع لميزات أفضل'
                : 'Tank capacity and height are recommended for better features',
              [
                { text: isArabic ? 'إلغاء' : 'Cancel', style: 'cancel' },
                { text: isArabic ? 'متابعة' : 'Continue', onPress: () => setCurrentStep(4) },
              ],
            );
            return false;
          }
        }
        return true;

      default:
        return true;
    }
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      if (currentStep < 4) {
        setCurrentStep(currentStep + 1);
      } else {
        handleCreateDevice();
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    } else {
      navigation.goBack();
    }
  };

  const handleCreateDevice = async () => {
    if (isSavingRef.current) {
      logger.warn('Already creating device, ignoring duplicate call');
      return;
    }

    try {
      isSavingRef.current = true;
      setSaving(true);

      const deviceData: any = {
        name: name.trim(),
        deviceType,
        hasBattery,
        siteId: selectedSiteId,
        zoneId: selectedZoneId,
        orgId: user?.orgId,
      };

      // Add tank data if applicable
      if (isTankDevice) {
        if (tankCapacityLiters.trim()) {
          deviceData.tankCapacityLiters = parseFloat(tankCapacityLiters);
        }
        if (tankHeightCm.trim()) {
          deviceData.tankHeightCm = parseFloat(tankHeightCm);
        }
        if (tankDiameterCm.trim()) {
          deviceData.tankDiameterCm = parseFloat(tankDiameterCm);
        }
        deviceData.tankShape = 'cylindrical';
      }

      logger.info('Creating device with wizard:', deviceData);

      const result = await deviceApi.createDevice(deviceData);

      // Create tank alerts if enabled
      if (isTankDevice && tankAlertsEnabled) {
        try {
          await alarmRulesApi.createRule({
            deviceId: result.id,
            type: 'level',
            severity: 'warning',
            thresholdLow: tankAlertLowCm.trim() ? parseFloat(tankAlertLowCm) : undefined,
            thresholdHigh: tankAlertHighCm.trim() ? parseFloat(tankAlertHighCm) : undefined,
          });
        } catch (error) {
          logger.warn('Failed to create tank alerts:', error);
        }
      }

      setCreatedDevice(result);
      openCredentials();
    } catch (error) {
      logger.error('Failed to create device:', error);
      Alert.alert(
        isArabic ? 'خطأ' : 'Error',
        isArabic ? 'فشل إنشاء الجهاز' : 'Failed to create device',
      );
    } finally {
      setSaving(false);
      isSavingRef.current = false;
    }
  };

  const handleCredentialsModalClose = () => {
    setShowCredentialsModal(false);
    // Navigate back to Devices screen and trigger refresh
    navigation.goBack();
  };

  const renderProgressBar = () => (
    <View style={[styles.progressContainer, { backgroundColor: theme.colors.card }]}>
      {steps.map((step, index) => (
        <View key={step.id} style={styles.stepContainer}>
          <View style={styles.stepCircleContainer}>
            {index > 0 && (
              <View
                style={[
                  styles.progressLine,
                  {
                    backgroundColor:
                      currentStep > step.id - 1 ? theme.colors.success : theme.colors.border,
                  },
                ]}
              />
            )}
            <TouchableOpacity
              style={[
                styles.stepCircle,
                {
                  backgroundColor:
                    currentStep === step.id
                      ? theme.colors.primary
                      : step.completed
                      ? theme.colors.success
                      : theme.colors.background,
                  borderColor:
                    currentStep === step.id || step.completed ? 'transparent' : theme.colors.border,
                },
              ]}
              disabled={!step.completed && currentStep !== step.id}
              onPress={() => step.completed && setCurrentStep(step.id)}
            >
              {step.completed ? (
                <Ionicons name="checkmark" size={16} color="#fff" />
              ) : (
                <Ionicons
                  name={step.icon as any}
                  size={16}
                  color={currentStep === step.id ? '#fff' : theme.colors.textSecondary}
                />
              )}
            </TouchableOpacity>
          </View>
          <Text
            style={[
              styles.stepLabel,
              {
                color:
                  currentStep === step.id || step.completed
                    ? theme.colors.text
                    : theme.colors.textSecondary,
                fontSize: currentStep === step.id ? 11 : 10,
                fontWeight: currentStep === step.id ? '600' : '400',
              },
            ]}
            numberOfLines={2}
          >
            {isArabic ? step.titleAr : step.title}
          </Text>
        </View>
      ))}
    </View>
  );

  const renderStep1BasicInfo = () => (
    <View style={styles.stepContent}>
      <View style={[styles.stepHeader, { backgroundColor: theme.colors.primary + '15' }]}>
        <Ionicons name="information-circle" size={32} color={theme.colors.primary} />
        <Text style={[styles.stepTitle, { color: theme.colors.text }]}>
          {isArabic ? 'المعلومات الأساسية' : 'Basic Information'}
        </Text>
        <Text style={[styles.stepDescription, { color: theme.colors.textSecondary }]}>
          {isArabic ? 'أدخل الاسم ونوع الجهاز' : 'Enter device name and type'}
        </Text>
      </View>

      {/* Device Name */}
      <View style={styles.fieldContainer}>
        <Text style={[styles.label, { color: theme.colors.text }]}>
          {isArabic ? 'اسم الجهاز' : 'Device Name'} *
        </Text>
        <TextInput
          style={[
            styles.input,
            {
              backgroundColor: theme.colors.background,
              color: theme.colors.text,
              borderColor: theme.colors.border,
            },
          ]}
          value={name}
          onChangeText={setName}
          placeholder={
            isArabic
              ? 'مثال: حساس درجة الحرارة - غرفة 101'
              : 'Example: Temperature Sensor - Room 101'
          }
          placeholderTextColor={theme.colors.textSecondary}
          autoFocus
        />
      </View>

      {/* Device Type */}
      <View style={styles.fieldContainer}>
        <Text style={[styles.label, { color: theme.colors.text }]}>
          {isArabic ? 'نوع الجهاز' : 'Device Type'} *
        </Text>
        <TouchableOpacity
          style={[
            styles.input,
            styles.pickerButton,
            { backgroundColor: theme.colors.background, borderColor: theme.colors.border },
          ]}
          onPress={() => openPicker(setShowDeviceTypePicker)}
        >
          <View style={styles.pickerContent}>
            <Ionicons
              name={getDeviceTypeIcon(deviceType) as any}
              size={20}
              color={theme.colors.primary}
            />
            <Text style={[styles.pickerText, { color: theme.colors.text }]}>
              {getDeviceTypeLabel(deviceType, isArabic ? 'ar' : 'en')}
            </Text>
          </View>
          <Ionicons name="chevron-down" size={20} color={theme.colors.textSecondary} />
        </TouchableOpacity>
      </View>

      {/* Has Battery */}
      <View
        style={[
          styles.switchContainer,
          { backgroundColor: theme.colors.background, borderColor: theme.colors.border },
        ]}
      >
        <View style={styles.switchLabel}>
          <Ionicons name="battery-half" size={24} color={theme.colors.primary} />
          <View style={styles.switchTextContainer}>
            <Text style={[styles.label, { marginBottom: 0, color: theme.colors.text }]}>
              {isArabic ? 'يحتوي على بطارية' : 'Battery Powered'}
            </Text>
            <Text style={[styles.hint, { marginTop: 2, color: theme.colors.textSecondary }]}>
              {isArabic ? 'الجهاز يعمل بالبطارية' : 'Device runs on battery'}
            </Text>
          </View>
        </View>
        <Switch
          value={hasBattery}
          onValueChange={setHasBattery}
          trackColor={{ false: theme.colors.border, true: theme.colors.primary + '80' }}
          thumbColor={hasBattery ? theme.colors.primary : theme.colors.textSecondary}
        />
      </View>
    </View>
  );

  const renderStep2Location = () => {
    const selectedSite = sites.find((s) => s.id === selectedSiteId);
    const selectedZone = zones.find((z) => z.id === selectedZoneId);

    return (
      <View style={styles.stepContent}>
        <View style={[styles.stepHeader, { backgroundColor: theme.colors.info + '15' }]}>
          <Ionicons name="location" size={32} color={theme.colors.info} />
          <Text style={[styles.stepTitle, { color: theme.colors.text }]}>
            {isArabic ? 'الموقع' : 'Location'}
          </Text>
          <Text style={[styles.stepDescription, { color: theme.colors.textSecondary }]}>
            {isArabic ? 'اختر الموقع والمنطقة' : 'Select site and zone'}
          </Text>
        </View>

        {/* Site Selector */}
        <View style={styles.fieldContainer}>
          <Text style={[styles.label, { color: theme.colors.text }]}>
            {isArabic ? 'الموقع' : 'Site'} *
          </Text>
          <TouchableOpacity
            style={[
              styles.input,
              styles.pickerButton,
              { backgroundColor: theme.colors.background, borderColor: theme.colors.border },
            ]}
            onPress={() => openPicker(setShowSitePicker)}
            disabled={loadingSites}
          >
            <View style={styles.pickerContent}>
              <Ionicons name="business" size={20} color={theme.colors.info} />
              <Text
                style={[
                  styles.pickerText,
                  { color: selectedSite ? theme.colors.text : theme.colors.textSecondary },
                ]}
              >
                {selectedSite ? selectedSite.name : isArabic ? 'اختر الموقع' : 'Select site'}
              </Text>
            </View>
            {loadingSites ? (
              <ActivityIndicator size="small" color={theme.colors.primary} />
            ) : (
              <Ionicons name="chevron-down" size={20} color={theme.colors.textSecondary} />
            )}
          </TouchableOpacity>
        </View>

        {/* Zone Selector */}
        {selectedSiteId && (
          <View style={styles.fieldContainer}>
            <Text style={[styles.label, { color: theme.colors.text }]}>
              {isArabic ? 'المنطقة' : 'Zone'} *
            </Text>
            <TouchableOpacity
              style={[
                styles.input,
                styles.pickerButton,
                { backgroundColor: theme.colors.background, borderColor: theme.colors.border },
              ]}
              onPress={() => openPicker(setShowZonePicker)}
              disabled={loadingZones || zones.length === 0}
            >
              <View style={styles.pickerContent}>
                <Ionicons name="grid" size={20} color={theme.colors.info} />
                <Text
                  style={[
                    styles.pickerText,
                    { color: selectedZone ? theme.colors.text : theme.colors.textSecondary },
                  ]}
                >
                  {selectedZone ? selectedZone.name : isArabic ? 'اختر المنطقة' : 'Select zone'}
                </Text>
              </View>
              {loadingZones ? (
                <ActivityIndicator size="small" color={theme.colors.primary} />
              ) : (
                <Ionicons name="chevron-down" size={20} color={theme.colors.textSecondary} />
              )}
            </TouchableOpacity>
            {zones.length === 0 && !loadingZones && (
              <Text style={[styles.hint, { color: theme.colors.warning }]}>
                {isArabic ? 'لا توجد مناطق في هذا الموقع' : 'No zones in this site'}
              </Text>
            )}
          </View>
        )}
      </View>
    );
  };

  const renderStep3Configuration = () => {
    if (!isTankDevice) {
      return (
        <View style={styles.stepContent}>
          <View style={[styles.stepHeader, { backgroundColor: theme.colors.success + '15' }]}>
            <Ionicons name="checkmark-circle" size={32} color={theme.colors.success} />
            <Text style={[styles.stepTitle, { color: theme.colors.text }]}>
              {isArabic ? 'جاهز للإنشاء' : 'Ready to Create'}
            </Text>
            <Text style={[styles.stepDescription, { color: theme.colors.textSecondary }]}>
              {isArabic ? 'يمكنك إنشاء الجهاز الآن' : 'You can create the device now'}
            </Text>
          </View>
        </View>
      );
    }

    return (
      <View style={styles.stepContent}>
        <View style={[styles.stepHeader, { backgroundColor: theme.colors.info + '15' }]}>
          <Ionicons name="water" size={32} color={theme.colors.info} />
          <Text style={[styles.stepTitle, { color: theme.colors.text }]}>
            {isArabic ? 'إعداد الخزان' : 'Tank Setup'}
          </Text>
          <Text style={[styles.stepDescription, { color: theme.colors.textSecondary }]}>
            {isArabic ? 'أدخل معلومات الخزان (اختياري)' : 'Enter tank information (optional)'}
          </Text>
        </View>

        {/* Tank Capacity */}
        <View style={styles.fieldContainer}>
          <Text style={[styles.label, { color: theme.colors.text }]}>
            {isArabic ? 'سعة الخزان (لتر)' : 'Tank Capacity (L)'}
          </Text>
          <TextInput
            style={[
              styles.input,
              {
                backgroundColor: theme.colors.background,
                color: theme.colors.text,
                borderColor: theme.colors.border,
              },
            ]}
            value={tankCapacityLiters}
            onChangeText={setTankCapacityLiters}
            placeholder="1000"
            placeholderTextColor={theme.colors.textSecondary}
            keyboardType="numeric"
          />
        </View>

        {/* Tank Height */}
        <View style={styles.fieldContainer}>
          <Text style={[styles.label, { color: theme.colors.text }]}>
            {isArabic ? 'ارتفاع الخزان (سم)' : 'Tank Height (cm)'}
          </Text>
          <TextInput
            style={[
              styles.input,
              {
                backgroundColor: theme.colors.background,
                color: theme.colors.text,
                borderColor: theme.colors.border,
              },
            ]}
            value={tankHeightCm}
            onChangeText={setTankHeightCm}
            placeholder="200"
            placeholderTextColor={theme.colors.textSecondary}
            keyboardType="numeric"
          />
        </View>

        {/* Tank Diameter */}
        <View style={styles.fieldContainer}>
          <Text style={[styles.label, { color: theme.colors.text }]}>
            {isArabic ? 'قطر الخزان (سم)' : 'Tank Diameter (cm)'}
          </Text>
          <TextInput
            style={[
              styles.input,
              {
                backgroundColor: theme.colors.background,
                color: theme.colors.text,
                borderColor: theme.colors.border,
              },
            ]}
            value={tankDiameterCm}
            onChangeText={setTankDiameterCm}
            placeholder="80"
            placeholderTextColor={theme.colors.textSecondary}
            keyboardType="numeric"
          />
        </View>

        {/* Tank Alerts Toggle */}
        <View
          style={[
            styles.switchContainer,
            { backgroundColor: theme.colors.background, borderColor: theme.colors.border },
          ]}
        >
          <View style={styles.switchLabel}>
            <Ionicons name="notifications" size={24} color={theme.colors.warning} />
            <View style={styles.switchTextContainer}>
              <Text style={[styles.label, { marginBottom: 0, color: theme.colors.text }]}>
                {isArabic ? 'تفعيل تنبيهات الخزان' : 'Enable Tank Alerts'}
              </Text>
              <Text style={[styles.hint, { marginTop: 2, color: theme.colors.textSecondary }]}>
                {isArabic ? 'تنبيهات عند انخفاض/ارتفاع المستوى' : 'Alerts for low/high levels'}
              </Text>
            </View>
          </View>
          <Switch
            value={tankAlertsEnabled}
            onValueChange={setTankAlertsEnabled}
            trackColor={{ false: theme.colors.border, true: theme.colors.warning + '80' }}
            thumbColor={tankAlertsEnabled ? theme.colors.warning : theme.colors.textSecondary}
          />
        </View>

        {/* Alert Thresholds */}
        {tankAlertsEnabled && (
          <View
            style={[
              styles.alertThresholdsContainer,
              { backgroundColor: theme.colors.background, borderColor: theme.colors.border },
            ]}
          >
            <View style={styles.fieldContainer}>
              <Text style={[styles.label, { color: theme.colors.text }]}>
                {isArabic ? 'تنبيه المستوى المنخفض (سم)' : 'Low Level Alert (cm)'}
              </Text>
              <TextInput
                style={[
                  styles.input,
                  {
                    backgroundColor: theme.colors.card,
                    color: theme.colors.text,
                    borderColor: theme.colors.border,
                  },
                ]}
                value={tankAlertLowCm}
                onChangeText={setTankAlertLowCm}
                placeholder="20"
                placeholderTextColor={theme.colors.textSecondary}
                keyboardType="numeric"
              />
            </View>

            <View style={styles.fieldContainer}>
              <Text style={[styles.label, { color: theme.colors.text }]}>
                {isArabic ? 'تنبيه المستوى المرتفع (سم)' : 'High Level Alert (cm)'}
              </Text>
              <TextInput
                style={[
                  styles.input,
                  {
                    backgroundColor: theme.colors.card,
                    color: theme.colors.text,
                    borderColor: theme.colors.border,
                  },
                ]}
                value={tankAlertHighCm}
                onChangeText={setTankAlertHighCm}
                placeholder="180"
                placeholderTextColor={theme.colors.textSecondary}
                keyboardType="numeric"
              />
            </View>
          </View>
        )}
      </View>
    );
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return renderStep1BasicInfo();
      case 2:
        return renderStep2Location();
      case 3:
        return renderStep3Configuration();
      default:
        return null;
    }
  };

  return (
    <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
      {/* Header */}
      <View
        style={[
          styles.header,
          { backgroundColor: theme.colors.card, borderBottomColor: theme.colors.border },
        ]}
      >
        <TouchableOpacity onPress={handleBack} style={styles.backButton}>
          <Ionicons
            name={isRTL ? 'chevron-forward' : 'chevron-back'}
            size={24}
            color={theme.colors.text}
          />
        </TouchableOpacity>
        <View style={styles.headerTitleContainer}>
          <Text style={[styles.headerTitle, { color: theme.colors.text }]}>
            {isArabic ? 'إضافة جهاز جديد' : 'Add New Device'}
          </Text>
          <Text style={[styles.headerSubtitle, { color: theme.colors.textSecondary }]}>
            {isArabic
              ? `الخطوة ${currentStep} من ${steps.length}`
              : `Step ${currentStep} of ${steps.length}`}
          </Text>
        </View>
      </View>

      {/* Progress Bar */}
      {renderProgressBar()}

      {/* Step Content */}
      <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
        {renderCurrentStep()}
      </ScrollView>

      {/* Action Buttons */}
      <View
        style={[
          styles.footer,
          { backgroundColor: theme.colors.card, borderTopColor: theme.colors.border },
        ]}
      >
        <TouchableOpacity
          style={[styles.secondaryButton, { borderColor: theme.colors.border }]}
          onPress={handleBack}
          disabled={saving}
        >
          <Text style={[styles.secondaryButtonText, { color: theme.colors.text }]}>
            {currentStep === 1 ? (isArabic ? 'إلغاء' : 'Cancel') : isArabic ? 'السابق' : 'Back'}
          </Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[styles.primaryButton, { backgroundColor: theme.colors.primary }]}
          onPress={handleNext}
          disabled={saving}
        >
          {saving ? (
            <ActivityIndicator size="small" color="#fff" />
          ) : (
            <>
              <Text style={styles.primaryButtonText}>
                {currentStep === 4 ? (isArabic ? 'إنشاء' : 'Create') : isArabic ? 'التالي' : 'Next'}
              </Text>
              {currentStep < 4 && (
                <Ionicons
                  name={isRTL ? 'chevron-back' : 'chevron-forward'}
                  size={20}
                  color="#fff"
                />
              )}
            </>
          )}
        </TouchableOpacity>
      </View>

      {/* MQTT Credentials Modal */}
      {createdDevice && (
        <MQTTCredentialsModal
          visible={showCredentialsModal}
          device={createdDevice}
          onClose={handleCredentialsModalClose}
        />
      )}

      {/* Device Type Picker Modal */}
      <Modal visible={showDeviceTypePicker} transparent animationType="slide">
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContent, { backgroundColor: theme.colors.card }]}>
            <View style={styles.modalHeader}>
              <Text style={[styles.modalTitle, { color: theme.colors.text }]}>
                {isArabic ? 'اختر نوع الجهاز' : 'Select Device Type'}
              </Text>
              <TouchableOpacity onPress={() => setShowDeviceTypePicker(false)}>
                <Ionicons name="close" size={24} color={theme.colors.text} />
              </TouchableOpacity>
            </View>
            <ScrollView>
              {Object.values(DeviceType).map((type) => (
                <TouchableOpacity
                  key={type}
                  style={[
                    styles.modalItem,
                    { borderBottomColor: theme.colors.border },
                    deviceType === type && { backgroundColor: theme.colors.primary + '15' },
                  ]}
                  onPress={() => {
                    setDeviceType(type);
                    setShowDeviceTypePicker(false);
                  }}
                >
                  <Ionicons
                    name={getDeviceTypeIcon(type) as any}
                    size={24}
                    color={deviceType === type ? theme.colors.primary : theme.colors.textSecondary}
                  />
                  <Text style={[styles.modalItemText, { color: theme.colors.text }]}>
                    {getDeviceTypeLabel(type, isArabic ? 'ar' : 'en')}
                  </Text>
                  {deviceType === type && (
                    <Ionicons name="checkmark-circle" size={20} color={theme.colors.primary} />
                  )}
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        </View>
      </Modal>

      {/* Site Picker Modal */}
      <Modal visible={showSitePicker} transparent animationType="slide">
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContent, { backgroundColor: theme.colors.card }]}>
            <View style={styles.modalHeader}>
              <Text style={[styles.modalTitle, { color: theme.colors.text }]}>
                {isArabic ? 'اختر الموقع' : 'Select Site'}
              </Text>
              <TouchableOpacity onPress={() => setShowSitePicker(false)}>
                <Ionicons name="close" size={24} color={theme.colors.text} />
              </TouchableOpacity>
            </View>
            <ScrollView>
              {sites.map((site) => (
                <TouchableOpacity
                  key={site.id}
                  style={[
                    styles.modalItem,
                    { borderBottomColor: theme.colors.border },
                    selectedSiteId === site.id && { backgroundColor: theme.colors.info + '15' },
                  ]}
                  onPress={() => {
                    setSelectedSiteId(site.id);
                    setSelectedZoneId(null); // Reset zone
                    setShowSitePicker(false);
                  }}
                >
                  <Ionicons
                    name="business"
                    size={24}
                    color={
                      selectedSiteId === site.id ? theme.colors.info : theme.colors.textSecondary
                    }
                  />
                  <Text style={[styles.modalItemText, { color: theme.colors.text }]}>
                    {site.name}
                  </Text>
                  {selectedSiteId === site.id && (
                    <Ionicons name="checkmark-circle" size={20} color={theme.colors.info} />
                  )}
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        </View>
      </Modal>

      {/* Zone Picker Modal */}
      <Modal visible={showZonePicker} transparent animationType="slide">
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContent, { backgroundColor: theme.colors.card }]}>
            <View style={styles.modalHeader}>
              <Text style={[styles.modalTitle, { color: theme.colors.text }]}>
                {isArabic ? 'اختر المنطقة' : 'Select Zone'}
              </Text>
              <TouchableOpacity onPress={() => setShowZonePicker(false)}>
                <Ionicons name="close" size={24} color={theme.colors.text} />
              </TouchableOpacity>
            </View>
            <ScrollView>
              {zones.map((zone) => (
                <TouchableOpacity
                  key={zone.id}
                  style={[
                    styles.modalItem,
                    { borderBottomColor: theme.colors.border },
                    selectedZoneId === zone.id && { backgroundColor: theme.colors.info + '15' },
                  ]}
                  onPress={() => {
                    setSelectedZoneId(zone.id);
                    setShowZonePicker(false);
                  }}
                >
                  <Ionicons
                    name="grid"
                    size={24}
                    color={
                      selectedZoneId === zone.id ? theme.colors.info : theme.colors.textSecondary
                    }
                  />
                  <Text style={[styles.modalItemText, { color: theme.colors.text }]}>
                    {zone.name}
                  </Text>
                  {selectedZoneId === zone.id && (
                    <Ionicons name="checkmark-circle" size={20} color={theme.colors.info} />
                  )}
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        </View>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingTop: Platform.OS === 'ios' ? 50 : 16,
    paddingBottom: 16,
    borderBottomWidth: 1,
  },
  backButton: {
    marginRight: 12,
  },
  headerTitleContainer: {
    flex: 1,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
  },
  headerSubtitle: {
    fontSize: 12,
    marginTop: 2,
  },
  progressContainer: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    paddingVertical: 20,
    paddingHorizontal: 8,
  },
  stepContainer: {
    flex: 1,
    alignItems: 'center',
  },
  stepCircleContainer: {
    alignItems: 'center',
    position: 'relative',
    width: '100%',
  },
  progressLine: {
    position: 'absolute',
    height: 2,
    width: '100%',
    top: 18,
    right: '50%',
  },
  stepCircle: {
    width: 36,
    height: 36,
    borderRadius: 18,
    borderWidth: 2,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1,
  },
  stepLabel: {
    marginTop: 8,
    textAlign: 'center',
    paddingHorizontal: 4,
  },
  content: {
    flex: 1,
  },
  stepContent: {
    padding: 20,
  },
  stepHeader: {
    alignItems: 'center',
    padding: 24,
    borderRadius: 16,
    marginBottom: 24,
  },
  stepTitle: {
    fontSize: 22,
    fontWeight: 'bold',
    marginTop: 12,
  },
  stepDescription: {
    fontSize: 14,
    marginTop: 8,
    textAlign: 'center',
  },
  fieldContainer: {
    marginBottom: 20,
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 8,
  },
  input: {
    borderWidth: 1,
    borderRadius: 12,
    padding: 14,
    fontSize: 16,
  },
  pickerButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  pickerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  pickerText: {
    fontSize: 16,
  },
  switchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    marginBottom: 20,
  },
  switchLabel: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    gap: 12,
  },
  switchTextContainer: {
    flex: 1,
  },
  hint: {
    fontSize: 12,
  },
  alertThresholdsContainer: {
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    marginTop: 12,
  },
  footer: {
    flexDirection: 'row',
    padding: 16,
    gap: 12,
    borderTopWidth: 1,
  },
  secondaryButton: {
    flex: 1,
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    alignItems: 'center',
  },
  secondaryButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  primaryButton: {
    flex: 2,
    padding: 16,
    borderRadius: 12,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  primaryButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: 'bold',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    maxHeight: '80%',
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
  },
  modalItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    gap: 12,
    borderBottomWidth: 1,
  },
  modalItemText: {
    flex: 1,
    fontSize: 16,
  },
});
